// next
import NextLink from 'next/link';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Link, Container, Typography } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// routes
import { PATH_AUTH } from '../../routes/paths';
// guards
import RegisterGuard from '../../guards/RegisterGuard';
// components
import Page from '../../components/Page';
import Image from '../../components/Image';
// sections
import { RegisterForm } from '../../sections/auth/register';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(2),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  maxHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function Register() {
  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');

  const theme = useTheme();

  return (
    <RegisterGuard>
      <Page title="가입">
        <RootStyle>
          <HeaderStyle>
            {!smUp && (
              <Typography variant="subtitle1" sx={{ fontFamily: theme.typography.subFontFamily }}>
                <Link underline="none" href="https://www.dio.so">DIO</Link>
              </Typography>
            )}
            <div />
            {smUp && (
              <Typography variant="body2" sx={{ mt: { md: -2 } }}>
                계정이 있으신가요?{' '}
                <NextLink href={PATH_AUTH.login} passHref>
                  <Link variant="subtitle2">로그인</Link>
                </NextLink>
              </Typography>
            )}
          </HeaderStyle>

          {mdUp && (
            <SectionStyle>
              <Image
                visibleByDefault
                disabledEffect
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crew-side.png"
                alt="register"
                sx={{ cursor: 'pointer' }}
                onClick={() => window.open("https://www.dio.so", '_self', 'noopener, noreferrer')}
              />
            </SectionStyle>
          )}

          <Container maxWidth="sm">
            <ContentStyle>
              <Box sx={{ mb: 5, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    {mdUp && (<Link underline="none" sx={{ fontFamily: theme.typography.subFontFamily }}>DIO</Link>)} 크루 지원하기
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>지원 결과를 확인하기 위한 이메일/비밀번호를 입력해주세요.</Typography>
                </Box>
              </Box>

              <RegisterForm />

              <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mt: 3 }}>
                크루 지원하기를 선택하여 <Link underline="none" color="text.secondary" sx={{ fontFamily: theme.typography.subFontFamily, fontWeight: 500}}>DIO</Link>의 {' '}
                <Link underline="always" color="text.primary" href="https://spacey-dio.notion.site/2025-10-01-232a8b46657c817a990aed013dcbec6e" target="_blank" rel="noopener">
                  이용약관
                </Link>
                과{' '}
                <Link underline="always" color="text.primary" href="https://spacey-dio.notion.site/3d3c141952c3409c9fdc1fff82ab0a36" target="_blank" rel="noopener">
                  개인정보 보호 정책
                </Link>
                에 동의합니다.
              </Typography>

              {!smUp && (
                <Typography variant="body2" sx={{ mt: 3, textAlign: 'center' }}>
                  계정이 있으신가요?{' '}
                  <NextLink href={PATH_AUTH.login} passHref>
                    <Link variant="subtitle2">로그인</Link>
                  </NextLink>
                </Typography>
              )}
            </ContentStyle>
          </Container>
        </RootStyle>
      </Page>
    </RegisterGuard>
  );
}
